import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/lib/auth-context';
import { dashboardAPI, complianceAPI, invoicesAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ClipboardList, Clock, FileText, Users, AlertCircle, CalendarDays, AlertTriangle, BarChart3, Bell, ChevronRight, Search, ArrowUpDown, ArrowUp, ArrowDown, Filter, GripVertical, Eye, EyeOff, Settings2, Save, Receipt, IndianRupee, ListChecks } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getDueDate, isOverdue, formatDueDate } from '@/lib/due-dates';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import logger from '@/lib/logger';

const STAGE_LABELS = {
  not_started: 'Not Started',
  documents_pending: 'Docs Pending',
  documents_received: 'Docs Received',
  ready_for_review: 'Ready for Review',
  ready_to_file: 'Ready to File',
  filed: 'Filed',
};

const SECTION_META = {
  stats_cards: { label: 'Stats Cards', icon: BarChart3 },
  compliance_grid: { label: 'Compliance Status', icon: CalendarDays },
  status_report: { label: 'Status Report', icon: ClipboardList },
  open_tasks: { label: 'Open Tasks', icon: ListChecks },
  pending_invoicing: { label: 'Pending Invoicing', icon: Receipt },
  pending_fees: { label: 'Pending Fees', icon: IndianRupee },
  recent_tasks: { label: 'Recent Tasks', icon: FileText },
  compliance_deadlines: { label: 'Compliance Deadlines', icon: AlertTriangle },
};
const ALL_SECTION_KEYS = ['stats_cards', 'compliance_grid', 'status_report', 'open_tasks', 'pending_invoicing', 'pending_fees', 'recent_tasks', 'compliance_deadlines'];

const STAGE_COLORS = {
  not_started: 'bg-gray-100 text-gray-700',
  documents_pending: 'bg-amber-100 text-amber-700',
  documents_received: 'bg-blue-100 text-blue-700',
  ready_for_review: 'bg-purple-100 text-purple-700',
  ready_to_file: 'bg-indigo-100 text-indigo-700',
  filed: 'bg-green-100 text-green-700',
};

const STATUS_TABS = [
  { key: 'documents_pending', label: 'Docs Pending', color: 'bg-amber-100 text-amber-700 border-amber-200', dot: 'bg-amber-500' },
  { key: 'documents_received', label: 'Docs Received', color: 'bg-blue-100 text-blue-700 border-blue-200', dot: 'bg-blue-500' },
  { key: 'further_docs_required', label: 'Further Docs Required', color: 'bg-orange-100 text-orange-700 border-orange-200', dot: 'bg-orange-500' },
  { key: 'ready_for_review', label: 'Review Pending', color: 'bg-purple-100 text-purple-700 border-purple-200', dot: 'bg-purple-500' },
  { key: 'tax_payable', label: 'Tax Payable', color: 'bg-rose-100 text-rose-700 border-rose-200', dot: 'bg-rose-500' },
  { key: 'ready_to_file', label: 'Ready to File', color: 'bg-indigo-100 text-indigo-700 border-indigo-200', dot: 'bg-indigo-500' },
  { key: 'filed', label: 'Filed', color: 'bg-green-100 text-green-700 border-green-200', dot: 'bg-green-500' },
];

const getMonthLabel = (period) => {
  if (!period || period === '-') return '-';
  // Handle YYYY-MM format
  if (/^\d{4}-\d{2}$/.test(period)) {
    const month = parseInt(period.split('-')[1]);
    const year = period.split('-')[0].slice(2);
    const names = { 1: 'Jan', 2: 'Feb', 3: 'Mar', 4: 'Apr', 5: 'May', 6: 'Jun', 7: 'Jul', 8: 'Aug', 9: 'Sep', 10: 'Oct', 11: 'Nov', 12: 'Dec' };
    return `${names[month]}'${year}`;
  }
  // Handle quarter format like Q1-2026
  if (/^Q\d/.test(period)) return period;
  // Handle FY format
  if (/^FY/.test(period)) return period;
  // Handle month_year format like apr_2026
  const match = period.match(/^([a-z]+)_(\d{4})$/i);
  if (match) {
    const mNames = { jan: 'Jan', feb: 'Feb', mar: 'Mar', apr: 'Apr', may: 'May', jun: 'Jun', jul: 'Jul', aug: 'Aug', sep: 'Sep', oct: 'Oct', nov: 'Nov', dec: 'Dec' };
    return `${mNames[match[1].toLowerCase()] || match[1]}'${match[2].slice(2)}`;
  }
  return period;
};

const getCellColor = (filed, total, pending) => {
  if (total === 0) return 'bg-gray-50 text-gray-300';
  if (filed === total) return 'bg-green-100 text-green-700';
  if (pending === total) return 'bg-gray-100 text-gray-500';
  const pct = filed / total;
  if (pct >= 0.75) return 'bg-green-50 text-green-600';
  if (pct >= 0.5) return 'bg-amber-50 text-amber-700';
  return 'bg-red-50 text-red-600';
};

const getInvoiceStatusStyle = (status) => {
  if (status === 'sent') return 'bg-amber-100 text-amber-700';
  if (status === 'partially_paid') return 'bg-blue-100 text-blue-700';
  return 'bg-gray-100 text-gray-700';
};

const getInvoiceStatusLabel = (status) => {
  if (status === 'partially_paid') return 'Partial';
  return status;
};

const getReminderStyle = (status) => {
  if (status === 'overdue') return { border: 'border-red-200 bg-red-50/50', Icon: AlertTriangle, iconClass: 'h-4 w-4 text-red-500 shrink-0' };
  if (status === 'due_today') return { border: 'border-orange-200 bg-orange-50/50', Icon: Clock, iconClass: 'h-4 w-4 text-orange-500 shrink-0' };
  return { border: 'hover:bg-muted/50', Icon: CalendarDays, iconClass: 'h-4 w-4 text-blue-500 shrink-0' };
};

const DashboardOverview = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [reminders, setReminders] = useState([]);
  const [complianceSummary, setComplianceSummary] = useState(null);
  const [statusReport, setStatusReport] = useState(null);
  const [activeStatusTab, setActiveStatusTab] = useState('documents_pending');
  const [reportSearch, setReportSearch] = useState('');
  const [reportFilterClient, setReportFilterClient] = useState('all');
  const [reportFilterBoard, setReportFilterBoard] = useState('all');
  const [reportFilterStaff, setReportFilterStaff] = useState('all');
  const [reportSort, setReportSort] = useState({ col: 'client_name', dir: 'asc' });
  const [uninvoicedItems, setUninvoicedItems] = useState([]);
  const [unpaidInvoices, setUnpaidInvoices] = useState([]);
  const [openTasksStageFilter, setOpenTasksStageFilter] = useState('all');
  const [openTasksSort, setOpenTasksSort] = useState({ col: 'client_name', dir: 'asc' });
  const [invoicingSort, setInvoicingSort] = useState({ col: 'client_name', dir: 'asc' });
  const [feesSort, setFeesSort] = useState({ col: 'invoice_number', dir: 'asc' });
  const [layoutEditing, setLayoutEditing] = useState(false);
  const [sectionOrder, setSectionOrder] = useState(ALL_SECTION_KEYS);
  const [roleVisibility, setRoleVisibility] = useState({ staff: ALL_SECTION_KEYS, client: ['stats_cards', 'recent_tasks'] });
  const [dragIdx, setDragIdx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingReminders, setSendingReminders] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const isAdminOrStaff = user?.role === 'admin' || user?.role === 'staff';
        const isAdmin = user?.role === 'admin';
        const hasBillingAccess = isAdmin || (user?.billing_client_ids || []).length > 0;

        const [statsRes, remindersRes, layoutRes] = await Promise.all([
          dashboardAPI.stats(), complianceAPI.reminders(), dashboardAPI.getLayout()
        ]);
        setStats(statsRes.data);
        setReminders(remindersRes.data);
        const layout = layoutRes.data;
        if (layout?.section_order) {
          const saved = layout.section_order;
          const missing = ALL_SECTION_KEYS.filter(k => !saved.includes(k));
          setSectionOrder([...saved, ...missing]);
        }
        if (layout?.role_visibility) setRoleVisibility(layout.role_visibility);

        if (isAdminOrStaff) {
          const extraPromises = [complianceAPI.summary('2026-27'), dashboardAPI.subtaskStatusReport()];
          if (isAdmin) extraPromises.push(invoicesAPI.uninvoicedSubtasks());
          if (hasBillingAccess) extraPromises.push(invoicesAPI.list());
          const extras = await Promise.all(extraPromises);
          setComplianceSummary(extras[0]?.data);
          setStatusReport(extras[1]?.data);
          let idx = 2;
          if (isAdmin) { setUninvoicedItems(extras[idx]?.data || []); idx++; }
          if (hasBillingAccess) {
            setUnpaidInvoices((extras[idx]?.data || []).filter(inv => inv.status === 'draft' || inv.status === 'sent' || inv.status === 'partially_paid'));
          }
        }
      } catch (err) {
        logger.error('Failed to load stats', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user?.role, user?.billing_client_ids]);

  const handleSortToggle = (col) => {
    setReportSort(prev => prev.col === col
      ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { col, dir: 'asc' }
    );
  };

  const handleDragStart = (idx) => setDragIdx(idx);
  const handleDragOver = (e, idx) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) return;
    setSectionOrder(prev => {
      const next = [...prev];
      const [item] = next.splice(dragIdx, 1);
      next.splice(idx, 0, item);
      setDragIdx(idx);
      return next;
    });
  };
  const handleDragEnd = () => setDragIdx(null);

  const toggleRoleVisibility = (role, key) => {
    setRoleVisibility(prev => {
      const arr = prev[role] || [];
      return { ...prev, [role]: arr.includes(key) ? arr.filter(k => k !== key) : [...arr, key] };
    });
  };

  const handleSaveLayout = async () => {
    try {
      await dashboardAPI.saveLayout({ section_order: sectionOrder, role_visibility: roleVisibility });
      toast.success('Dashboard layout saved');
      setLayoutEditing(false);
    } catch { toast.error('Failed to save layout'); }
  };

  const filteredReportItems = useMemo(() => {
    if (!statusReport?.report) return [];
    let items = statusReport.report[activeStatusTab] || [];
    if (reportFilterClient !== 'all') items = items.filter(i => i.client_id === reportFilterClient);
    if (reportFilterBoard !== 'all') items = items.filter(i => i.board_id === reportFilterBoard);
    if (reportFilterStaff !== 'all') items = items.filter(i => i.assigned_to === reportFilterStaff);
    if (reportSearch.trim()) {
      const q = reportSearch.toLowerCase();
      items = items.filter(i => i.client_name.toLowerCase().includes(q) || i.subtask_title.toLowerCase().includes(q) || i.board_name.toLowerCase().includes(q));
    }
    const { col, dir } = reportSort;
    items = [...items].sort((a, b) => {
      const va = (a[col] || '').toLowerCase();
      const vb = (b[col] || '').toLowerCase();
      return dir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
    });
    return items;
  }, [statusReport, activeStatusTab, reportFilterClient, reportFilterBoard, reportFilterStaff, reportSearch, reportSort]);

  const attentionSummary = useMemo(() => {
    if (!statusReport?.summary) return { total: 0, pending: 0, received: 0, review: 0, filed: 0 };
    const s = statusReport.summary;
    return {
      total: Object.entries(s).filter(([k]) => k !== 'filed').reduce((sum, [, v]) => sum + v, 0),
      pending: s.documents_pending || 0,
      received: s.documents_received || 0,
      review: (s.ready_for_review || 0) + (s.further_docs_required || 0),
      filed: s.filed || 0,
    };
  }, [statusReport]);

  // Open Tasks: all items NOT filed, with stage filter
  const openTaskItems = useMemo(() => {
    if (!statusReport?.report) return [];
    let items = [];
    for (const [status, list] of Object.entries(statusReport.report)) {
      if (status === 'filed') continue;
      for (const item of list) items.push({ ...item, status });
    }
    if (openTasksStageFilter !== 'all') items = items.filter(i => i.status === openTasksStageFilter);
    items.sort((a, b) => a.client_name.localeCompare(b.client_name) || a.subtask_title.localeCompare(b.subtask_title));
    return items;
  }, [statusReport, openTasksStageFilter]);

  const openTaskStageCounts = useMemo(() => {
    if (!statusReport?.summary) return {};
    const counts = {};
    for (const [k, v] of Object.entries(statusReport.summary)) {
      if (k !== 'filed') counts[k] = v;
    }
    return counts;
  }, [statusReport]);

  const totalOpenStages = useMemo(() => Object.values(openTaskStageCounts).reduce((s, v) => s + v, 0), [openTaskStageCounts]);

  const totalOutstanding = useMemo(() => unpaidInvoices.reduce((s, i) => s + Number(i.total_value || i.amount || 0), 0), [unpaidInvoices]);

  // Generic sort helper for the 3 dashboard tables (Open Tasks, Invoicing, Fees)
  const applySort = (rows, sort) => {
    if (!sort?.col) return rows;
    const sign = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const va = a[sort.col];
      const vb = b[sort.col];
      const sa = va === null || va === undefined ? '' : String(va).toLowerCase();
      const sb = vb === null || vb === undefined ? '' : String(vb).toLowerCase();
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * sign;
      return sa.localeCompare(sb, undefined, { numeric: true }) * sign;
    });
  };
  const SortHead = ({ col, label, sort, setSort, className = '' }) => {
    const active = sort.col === col;
    const Icon = active ? (sort.dir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
    const onClick = () => setSort(prev => prev.col === col ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' });
    return (
      <TableHead className={`text-xs font-semibold cursor-pointer select-none hover:bg-muted/50 ${className}`} onClick={onClick} data-testid={`dash-sort-${col}`}>
        <span className={`flex items-center gap-1 ${active ? 'text-foreground' : 'text-muted-foreground'}`}>
          {label}
          <Icon className={`h-3.5 w-3.5 ${active ? 'text-primary' : 'opacity-70'}`} />
        </span>
      </TableHead>
    );
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-24 bg-muted rounded-xl animate-pulse" />)}
      </div>
    );
  }

  const role = user?.role;

  const handleSendReminders = async () => {
    setSendingReminders(true);
    try {
      const res = await complianceAPI.sendReminders();
      const count = res.data.count || 0;
      if (count > 0) {
        toast.success(`${count} reminder(s) sent to client chat threads`);
      } else {
        toast.info('No pending reminders to send');
      }
    } catch (err) {
      toast.error('Failed to send reminders');
    }
    setSendingReminders(false);
  };

  const adminStats = [
    { icon: Users, label: 'Total Clients', value: stats?.total_clients || 0, color: 'text-blue-600', link: '/dashboard/clients' },
    { icon: ClipboardList, label: 'Open Tasks', value: stats?.open_tasks || 0, color: 'text-amber-600', link: '/dashboard/open-tasks' },
    { icon: FileText, label: 'Pending Invoices', value: stats?.pending_invoices || 0, color: 'text-purple-600', link: '/dashboard/pending-invoices' },
    { icon: AlertCircle, label: 'Pending Fees', value: stats?.pending_fees || 0, color: 'text-red-500', link: '/dashboard/pending-fees' },
  ];

  const staffHasBilling = (user?.billing_client_ids || []).length > 0;
  const staffStats = [
    { icon: ClipboardList, label: 'My Tasks', value: stats?.my_tasks || 0, color: 'text-blue-600', link: '/dashboard/my-tasks' },
    { icon: Clock, label: 'Open Tasks', value: stats?.open_tasks || 0, color: 'text-amber-600', link: '/dashboard/open-tasks' },
    { icon: AlertCircle, label: 'Docs Pending', value: stats?.docs_pending || 0, color: 'text-red-500', link: '/dashboard/open-tasks' },
    ...(staffHasBilling ? [{ icon: IndianRupee, label: 'Pending Fees', value: unpaidInvoices.length, color: 'text-rose-500', link: '/dashboard/pending-fees' }] : [{ icon: FileText, label: 'Filed', value: stats?.filed_tasks || 0, color: 'text-green-600', link: '/dashboard/boards' }]),
  ];

  const clientStats = [
    { icon: ClipboardList, label: 'Total Tasks', value: stats?.total_tasks || 0, color: 'text-blue-600', link: '/dashboard/boards' },
    { icon: Clock, label: 'Open Tasks', value: stats?.open_tasks || 0, color: 'text-amber-600', link: '/dashboard/boards' },
    { icon: AlertCircle, label: 'Docs Pending', value: stats?.docs_pending || 0, color: 'text-red-500', link: '/dashboard/boards' },
    { icon: FileText, label: 'Filed', value: stats?.filed_tasks || 0, color: 'text-green-600', link: '/dashboard/boards' },
  ];

  const displayStats = { admin: adminStats, staff: staffStats, client: clientStats }[role] || clientStats;

  // Get summary rows from compliance summary
  const activeSummaryRows = complianceSummary?.summary || [];

  // Group by frequency
  const monthlyRows = activeSummaryRows.filter(r => (r.frequency || 'monthly') === 'monthly');
  const quarterlyRows = activeSummaryRows.filter(r => r.frequency === 'quarterly');
  const annualRows = activeSummaryRows.filter(r => r.frequency === 'annual');
  const oneTimeRows = activeSummaryRows.filter(r => r.frequency === 'one_time');

  const getQLabel = (p) => {
    const labels = { Q1: 'Q1 (Apr-Jun)', Q2: 'Q2 (Jul-Sep)', Q3: 'Q3 (Oct-Dec)', Q4: 'Q4 (Jan-Mar)' };
    return labels[p.split('-')[1]] || p;
  };

  const renderGridSection = (rows, periods, label, periodLabelFn, colWidth, frequency) => {
    if (rows.length === 0) return null;
    return (
      <div className="mb-4 last:mb-0">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{label}</span>
          <span className="text-[10px] text-muted-foreground">({rows.length} return types)</span>
        </div>
        <div className="overflow-x-auto">
        <div style={{ width: '100%', minWidth: `${180 + periods.length * colWidth}px` }}>
          <div className="flex items-center border-b bg-muted/30 rounded-t-lg">
            <div className="flex-1 min-w-[176px] px-4 py-2.5 text-xs font-semibold text-muted-foreground sticky left-0 bg-muted/30 z-10">Return Type</div>
            {periods.map(period => (
              <div key={period} style={{ width: `${colWidth}px` }} className="flex-shrink-0 text-center py-2.5 text-[11px] font-semibold text-muted-foreground border-l">
                {periodLabelFn(period)}
              </div>
            ))}
          </div>
          {rows.map((row, i) => (
            <div key={row.subtask_title} className={`flex items-center border-b last:border-b-0 ${i % 2 === 0 ? '' : 'bg-muted/10'}`}>
              <div className="flex-1 min-w-[176px] px-4 py-2 sticky left-0 bg-background z-10">
                <p className="text-sm font-medium">{row.subtask_title}</p>
                <p className="text-[10px] text-muted-foreground">{row.board_name}</p>
              </div>
              {periods.map(period => {
                const pData = row.periods[period] || { total: 0, filed: 0, pending: 0, in_progress: 0 };
                const dueDate = getDueDate(row.subtask_title, period, frequency, row.due_date_rule);
                const overdue = isOverdue(dueDate) && pData.total > 0 && pData.filed < pData.total;
                const cellColor = overdue ? 'bg-red-100 text-red-700 ring-1 ring-red-200' : getCellColor(pData.filed, pData.total, pData.pending);
                return (
                  <div key={period} style={{ width: `${colWidth}px` }} className="flex-shrink-0 border-l px-1 py-1.5">
                    <div className={`h-8 flex flex-col items-center justify-center rounded text-[10px] font-medium ${cellColor}`}>
                      {pData.total > 0 ? (<><span className="font-bold leading-none">{pData.filed}/{pData.total}</span><span className="leading-none opacity-70">{overdue ? 'overdue' : 'filed'}</span></>) : (<span>-</span>)}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        </div>
      </div>
    );
  };

  // Section renderers
  const renderSection = (key) => {
    switch (key) {
      case 'stats_cards':
        return (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" key={key}>
            {displayStats.map((stat) => (
              <Card key={stat.label} className="transition-all duration-200 hover:shadow-[var(--shadow-md)] hover:-translate-y-0.5 cursor-pointer group" onClick={() => stat.link && navigate(stat.link)} data-testid={`stat-card-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}>
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <stat.icon className={`h-5 w-5 ${stat.color}`} />
                    <ChevronRight className="h-4 w-4 text-muted-foreground/0 group-hover:text-muted-foreground/60 transition-all" />
                  </div>
                  <p className="text-2xl font-bold">{stat.value}</p>
                  <p className="text-xs text-muted-foreground mt-1">{stat.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        );

      case 'compliance_grid':
        if (!(role === 'admin' || role === 'staff') || !complianceSummary || activeSummaryRows.length === 0) return null;
        return (
        <Card data-testid="compliance-grid">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-[#14B8A6]" />
              Compliance Status — FY 2026-27
            </CardTitle>
            <div className="flex items-center gap-4">
              {role === 'admin' && (
                <Button variant="outline" size="sm" className="gap-1.5 text-xs" onClick={handleSendReminders} disabled={sendingReminders} data-testid="send-reminders-btn">
                  <Bell className="h-3.5 w-3.5" />
                  {sendingReminders ? 'Sending...' : 'Send Reminders'}
                </Button>
              )}
              <div className="flex items-center gap-3 text-[11px]">
              <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-green-100 border border-green-200" /> All Filed</span>
              <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-amber-50 border border-amber-200" /> Partial</span>
              <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-red-50 border border-red-200" /> Mostly Pending</span>
              <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-gray-50 border border-gray-200" /> Not Started</span>
              <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-red-100 border border-red-300 ring-1 ring-red-200" /> Overdue</span>
            </div>
            </div>
          </CardHeader>
          <CardContent>
            <ScrollArea className="w-full">
              {renderGridSection(monthlyRows, complianceSummary.periods, 'Monthly Returns', getMonthLabel, 64, 'monthly')}
              {renderGridSection(quarterlyRows, complianceSummary.quarterly_periods || [], 'Quarterly Returns', getQLabel, 110, 'quarterly')}
              {renderGridSection(annualRows, complianceSummary.annual_periods || [], 'Annual Returns', (p) => `FY ${p.replace('FY', '')}`, 130, 'annual')}
              {renderGridSection(oneTimeRows, ['OT-2026-27'], 'One Time', () => 'One Time', 130, 'one_time')}
              <ScrollBar orientation="horizontal" />
            </ScrollArea>
          </CardContent>
        </Card>
        );

      case 'status_report':
        if (!(role === 'admin' || role === 'staff') || !statusReport) return null;
        return (
        <Card data-testid="subtask-status-report" key={key}>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-amber-600" />
              Open Tasks — Status Report
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Summary Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-amber-700">{attentionSummary.total}</p>
                <p className="text-[11px] text-amber-600 font-medium">Need Attention</p>
              </div>
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-orange-700">{attentionSummary.pending}</p>
                <p className="text-[11px] text-orange-600 font-medium">Docs Pending</p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-blue-700">{attentionSummary.received}</p>
                <p className="text-[11px] text-blue-600 font-medium">Docs Received</p>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-green-700">{attentionSummary.filed}</p>
                <p className="text-[11px] text-green-600 font-medium">Filed</p>
              </div>
            </div>

            {/* Status Tabs */}
            <div className="flex flex-wrap gap-2">
              {STATUS_TABS.map(tab => {
                const count = statusReport.summary?.[tab.key] || 0;
                const isActive = activeStatusTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    onClick={() => setActiveStatusTab(tab.key)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                      isActive ? `${tab.color} ring-2 ring-offset-1 ring-current/20` : 'bg-muted/50 text-muted-foreground border-transparent hover:bg-muted'
                    }`}
                    data-testid={`status-tab-${tab.key}`}
                  >
                    <span className={`w-2 h-2 rounded-full ${tab.dot}`} />
                    {tab.label}
                    <span className={`ml-0.5 text-[10px] font-bold ${isActive ? '' : 'text-muted-foreground'}`}>({count})</span>
                  </button>
                );
              })}
            </div>

            {/* Filters & Search */}
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative flex-1 min-w-[180px] max-w-[280px]">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  placeholder="Search client or subtask..."
                  value={reportSearch}
                  onChange={e => setReportSearch(e.target.value)}
                  className="pl-8 h-8 text-xs"
                  data-testid="report-search-input"
                />
              </div>
              <Select value={reportFilterClient} onValueChange={setReportFilterClient}>
                <SelectTrigger className="w-[160px] h-8 text-xs" data-testid="report-filter-client">
                  <SelectValue placeholder="All Clients" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Clients</SelectItem>
                  {statusReport.filters?.clients?.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={reportFilterBoard} onValueChange={setReportFilterBoard}>
                <SelectTrigger className="w-[140px] h-8 text-xs" data-testid="report-filter-board">
                  <SelectValue placeholder="All Boards" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Boards</SelectItem>
                  {statusReport.filters?.boards?.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={reportFilterStaff} onValueChange={setReportFilterStaff}>
                <SelectTrigger className="w-[140px] h-8 text-xs" data-testid="report-filter-staff">
                  <SelectValue placeholder="All Team" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Team</SelectItem>
                  {statusReport.filters?.staff?.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {(reportSearch || reportFilterClient !== 'all' || reportFilterBoard !== 'all' || reportFilterStaff !== 'all') && (
                <Button variant="ghost" size="sm" className="h-8 text-xs px-2" onClick={() => { setReportSearch(''); setReportFilterClient('all'); setReportFilterBoard('all'); setReportFilterStaff('all'); }}>
                  Clear
                </Button>
              )}
              <span className="text-[11px] text-muted-foreground ml-auto">{filteredReportItems.length} item(s)</span>
            </div>

            {/* Sortable Table */}
            {filteredReportItems.length > 0 ? (
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/30">
                      {[
                        { key: 'client_name', label: 'Client', w: 'w-[170px]' },
                        { key: 'subtask_title', label: 'Subtask', w: '' },
                        { key: 'board_name', label: 'Board', w: '' },
                        { key: 'period', label: 'Period', w: 'w-[90px]' },
                        { key: 'staff_name', label: 'Assigned', w: '' },
                        { key: 'frequency', label: 'Freq', w: 'w-[80px]' },
                      ].map(col => (
                        <TableHead key={col.key} className={`text-xs font-semibold cursor-pointer select-none hover:bg-muted/50 ${col.w}`} onClick={() => handleSortToggle(col.key)} data-testid={`status-report-sort-${col.key}`}>
                          <span className={`flex items-center gap-1 ${reportSort.col === col.key ? 'text-foreground' : 'text-muted-foreground'}`}>
                            {col.label}
                            {reportSort.col === col.key
                              ? (reportSort.dir === 'asc' ? <ArrowUp className="h-3.5 w-3.5 text-primary" /> : <ArrowDown className="h-3.5 w-3.5 text-primary" />)
                              : <ArrowUpDown className="h-3.5 w-3.5 opacity-70" />}
                          </span>
                        </TableHead>
                      ))}
                      <TableHead className="w-[30px]" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredReportItems.map((item, idx) => (
                      <TableRow
                        key={`${item.subtask_id}-${item.client_id}-${item.period}`}
                        className="hover:bg-muted/30 cursor-pointer group"
                        onClick={() => navigate(`/dashboard/boards/${item.board_id}/task/${item.task_id}/subtask/${item.subtask_id}`)}
                        data-testid={`report-row-${idx}`}
                      >
                        <TableCell className="text-sm font-medium">{item.client_name}</TableCell>
                        <TableCell className="text-sm">{item.subtask_title}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{item.board_name}</TableCell>
                        <TableCell className="text-xs">{item.period !== '-' ? getMonthLabel(item.period) : '-'}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{item.staff_name || '-'}</TableCell>
                        <TableCell><Badge variant="secondary" className="text-[10px]">{item.frequency}</Badge></TableCell>
                        <TableCell><ChevronRight className="h-3.5 w-3.5 text-muted-foreground/30 group-hover:text-muted-foreground transition-colors" /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground text-sm">
                {reportSearch || reportFilterClient !== 'all' || reportFilterBoard !== 'all' || reportFilterStaff !== 'all'
                  ? 'No items match filters'
                  : `No subtasks with "${STATUS_TABS.find(t => t.key === activeStatusTab)?.label}" status`}
              </div>
            )}
          </CardContent>
        </Card>
        );

      case 'open_tasks':
        if (!(role === 'admin' || role === 'staff') || !statusReport) return null;
        return (
        <Card key={key} data-testid="open-tasks-section">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <ListChecks className="h-5 w-5 text-orange-600" />
                Open Tasks
                <Badge variant="secondary" className="ml-1 text-xs">{openTaskItems.length}</Badge>
              </CardTitle>
              <Select value={openTasksStageFilter} onValueChange={setOpenTasksStageFilter}>
                <SelectTrigger className="w-[180px] h-8 text-xs" data-testid="open-tasks-stage-filter"><SelectValue placeholder="All Stages" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Stages ({totalOpenStages})</SelectItem>
                  {Object.entries(openTaskStageCounts).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{STAGE_LABELS[k] || k} ({v})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            {openTaskItems.length > 0 ? (
              <div className="border rounded-lg overflow-hidden max-h-[320px] overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/30">
                      <SortHead col="client_name" label="Client" sort={openTasksSort} setSort={setOpenTasksSort} />
                      <SortHead col="subtask_title" label="Subtask" sort={openTasksSort} setSort={setOpenTasksSort} />
                      <SortHead col="board_name" label="Board" sort={openTasksSort} setSort={setOpenTasksSort} />
                      <SortHead col="period" label="Period" sort={openTasksSort} setSort={setOpenTasksSort} />
                      <SortHead col="status" label="Stage" sort={openTasksSort} setSort={setOpenTasksSort} />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {applySort(openTaskItems, openTasksSort).slice(0, 50).map((item, idx) => (
                      <TableRow key={`${item.subtask_id}-${item.client_id}-${idx}`} className="hover:bg-muted/30 cursor-pointer" onClick={() => navigate(`/dashboard/boards/${item.board_id}/task/${item.task_id}/subtask/${item.subtask_id}`)}>
                        <TableCell className="text-sm font-medium">{item.client_name}</TableCell>
                        <TableCell className="text-sm">{item.subtask_title}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{item.board_name}</TableCell>
                        <TableCell className="text-xs">{item.period !== '-' ? getMonthLabel(item.period) : '-'}</TableCell>
                        <TableCell><Badge variant="secondary" className={`text-[10px] ${STAGE_COLORS[item.status] || ''}`}>{STAGE_LABELS[item.status] || item.status}</Badge></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <p className="text-center py-6 text-sm text-muted-foreground">No open tasks{openTasksStageFilter !== 'all' ? ' for this stage' : ''}</p>
            )}
          </CardContent>
        </Card>
        );

      case 'pending_invoicing':
        if (role === 'staff') return null;  // Staff uses Pending Fees page only
        if (role !== 'admin' || uninvoicedItems.length === 0) return null;
        return (
        <Card key={key} data-testid="pending-invoicing-section">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Receipt className="h-5 w-5 text-violet-600" />
                Pending Invoicing
                <Badge variant="secondary" className="ml-1 text-xs bg-violet-100 text-violet-700">{uninvoicedItems.length}</Badge>
              </CardTitle>
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => navigate('/dashboard/billing')}>View All</Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg overflow-hidden max-h-[280px] overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/30">
                    <SortHead col="client_name" label="Client" sort={invoicingSort} setSort={setInvoicingSort} />
                    <SortHead col="subtask_title" label="Subtask" sort={invoicingSort} setSort={setInvoicingSort} />
                    <SortHead col="board_name" label="Board" sort={invoicingSort} setSort={setInvoicingSort} />
                    <TableHead className="text-xs font-semibold">Filed Periods</TableHead>
                    <TableHead className="text-xs font-semibold">Period Range</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {applySort(uninvoicedItems, invoicingSort).slice(0, 20).map((item, idx) => (
                    <TableRow key={`${item.subtask_id}-${item.client_id}`} className="hover:bg-muted/30 cursor-pointer" onClick={() => navigate('/dashboard/billing')}>
                      <TableCell className="text-sm font-medium">{item.client_name || item.client_id?.slice(0, 8)}</TableCell>
                      <TableCell className="text-sm">{item.subtask_title}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{item.board_name}</TableCell>
                      <TableCell className="text-xs text-center">{item.filed_periods?.length || 0}</TableCell>
                      <TableCell className="text-xs">{item.from_period ? `${getMonthLabel(item.from_period)} — ${getMonthLabel(item.to_period)}` : '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
        );

      case 'pending_fees': {
        const hasBillingAccess = role === 'admin' || (role === 'staff' && (user?.billing_client_ids || []).length > 0);
        if (!hasBillingAccess || unpaidInvoices.length === 0) return null;
        return (
        <Card key={key} data-testid="pending-fees-section">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <IndianRupee className="h-5 w-5 text-rose-600" />
                Pending Fees (Receivables)
                <Badge variant="secondary" className="ml-1 text-xs bg-rose-100 text-rose-700">{unpaidInvoices.length}</Badge>
              </CardTitle>
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => navigate(role === 'staff' ? '/dashboard/pending-fees' : '/dashboard/billing')}>View All</Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg overflow-hidden max-h-[280px] overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/30">
                    <SortHead col="invoice_number" label="Invoice #" sort={feesSort} setSort={setFeesSort} />
                    <SortHead col="client_name" label="Client" sort={feesSort} setSort={setFeesSort} />
                    <SortHead col="service_description" label="Service" sort={feesSort} setSort={setFeesSort} />
                    <SortHead col="status" label="Status" sort={feesSort} setSort={setFeesSort} />
                    {role === 'admin' && <SortHead col="total_value" label="Amount" sort={feesSort} setSort={setFeesSort} className="text-right" />}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {applySort(unpaidInvoices, feesSort).slice(0, 20).map((inv, idx) => (
                    <TableRow key={inv.id || inv.invoice_number} className="hover:bg-muted/30 cursor-pointer" onClick={() => navigate(role === 'staff' ? '/dashboard/pending-fees' : '/dashboard/billing')}>
                      <TableCell className="text-sm font-mono">{inv.invoice_number}</TableCell>
                      <TableCell className="text-sm font-medium">{inv.client_name || '-'}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{inv.service_description || '-'}</TableCell>
                      <TableCell><Badge variant="secondary" className={`text-[10px] ${getInvoiceStatusStyle(inv.status)}`}>{getInvoiceStatusLabel(inv.status)}</Badge></TableCell>
                      {role === 'admin' && <TableCell className="text-sm text-right font-medium">Rs. {Number(inv.total_value || inv.amount || 0).toLocaleString('en-IN')}</TableCell>}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {role === 'admin' && (
              <div className="mt-2 text-right text-xs text-muted-foreground">
                Total outstanding: <span className="font-semibold text-foreground">Rs. {totalOutstanding.toLocaleString('en-IN')}</span>
              </div>
            )}
          </CardContent>
        </Card>
        );
      }

      case 'recent_tasks':
        if (!stats?.recent_tasks?.length) return null;
        return (
        <Card key={key}>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">Recent Tasks</CardTitle>
            <button className="text-sm text-primary hover:underline" onClick={() => navigate('/dashboard/boards')}>View all</button>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats.recent_tasks.map(task => (
                <div key={task.id} className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 transition-colors duration-150 cursor-pointer" onClick={() => navigate(`/dashboard/boards`)}>
                  <div>
                    <p className="font-medium text-sm">{task.title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{task.due_date ? `Due: ${task.due_date}` : 'No due date'}</p>
                  </div>
                  <Badge variant="secondary" className={`text-xs ${STAGE_COLORS[task.stage]}`}>{STAGE_LABELS[task.stage]}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        );

      case 'compliance_deadlines':
        if (!reminders.length) return null;
        return (
        <Card data-testid="dashboard-compliance-deadlines" key={key}>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <CalendarDays className="h-5 w-5 text-amber-600" />
              Upcoming Compliance Deadlines
            </CardTitle>
            <button className="text-sm text-primary hover:underline" onClick={() => navigate('/dashboard/calendar')}>View Calendar</button>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {reminders.slice(0, 5).map(r => {
                const style = getReminderStyle(r.status);
                const dueStr = r.compliance_due_date ? String(r.compliance_due_date).slice(0, 10) : null;
                const overdue = dueStr ? isOverdue(dueStr) : false;
                const today = new Date(); today.setHours(0, 0, 0, 0);
                const isDueToday = dueStr ? new Date(dueStr + 'T00:00:00').getTime() === today.getTime() : false;
                const isOverdue_ = overdue;
                return (
                  <div
                    key={r.id}
                    className={`flex items-center justify-between p-3 rounded-lg border transition-colors duration-150 ${style.border}`}
                    data-testid={`dashboard-reminder-${r.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <style.Icon className={style.iconClass} />
                      <div>
                        <p className="font-medium text-sm">{r.title}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {r.client_name && `${r.client_name} · `}
                          Due: {new Date(r.compliance_due_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className={`text-xs ${STAGE_COLORS[r.stage]}`}>{STAGE_LABELS[r.stage]}</Badge>
                      {(isOverdue_ || isDueToday) && (
                        <Badge variant="secondary" className={`text-xs ${isOverdue_ ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'}`}>
                          {isOverdue_ ? 'Overdue' : 'Today'}
                        </Badge>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
        );

      default:
        return null;
    }
  };

  // Determine visible sections based on role
  const visibleOrder = role === 'admin'
    ? sectionOrder
    : sectionOrder.filter(k => (roleVisibility[role] || []).includes(k));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl sm:text-2xl font-semibold">Welcome, {user?.name}</h2>
          <p className="text-muted-foreground text-sm mt-1">Here's your overview for today</p>
        </div>
        {role === 'admin' && (
          layoutEditing ? (
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setLayoutEditing(false)} data-testid="layout-cancel-btn">Cancel</Button>
              <Button size="sm" onClick={handleSaveLayout} data-testid="layout-save-btn"><Save className="h-3.5 w-3.5 mr-1" /> Save Layout</Button>
            </div>
          ) : (
            <Button size="sm" variant="outline" onClick={() => setLayoutEditing(true)} data-testid="layout-edit-btn">
              <Settings2 className="h-3.5 w-3.5 mr-1" /> Edit Layout
            </Button>
          )
        )}
      </div>

      {/* Layout Editor Panel */}
      {layoutEditing && role === 'admin' && (
        <Card className="border-dashed border-2 border-primary/30 bg-primary/5">
          <CardContent className="p-4 space-y-3">
            <p className="text-xs font-semibold text-primary">Drag sections to reorder. Toggle visibility for Staff and Client roles.</p>
            <div className="space-y-1.5">
              {sectionOrder.map((key, idx) => {
                const meta = SECTION_META[key];
                if (!meta) return null;
                const Icon = meta.icon;
                return (
                  <div
                    key={key}
                    draggable
                    onDragStart={() => handleDragStart(idx)}
                    onDragOver={(e) => handleDragOver(e, idx)}
                    onDragEnd={handleDragEnd}
                    className={`flex items-center gap-3 p-2.5 rounded-lg border bg-background transition-all ${dragIdx === idx ? 'ring-2 ring-primary shadow-md scale-[1.02]' : 'hover:bg-muted/50'}`}
                    data-testid={`layout-section-${key}`}
                  >
                    <GripVertical className="h-4 w-4 text-muted-foreground cursor-grab shrink-0" />
                    <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className="text-sm font-medium flex-1">{meta.label}</span>
                    <div className="flex items-center gap-3">
                      <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                        <Switch
                          checked={(roleVisibility.staff || []).includes(key)}
                          onCheckedChange={() => toggleRoleVisibility('staff', key)}
                          className="scale-75"
                          data-testid={`vis-staff-${key}`}
                        />
                        Staff
                      </label>
                      <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                        <Switch
                          checked={(roleVisibility.client || []).includes(key)}
                          onCheckedChange={() => toggleRoleVisibility('client', key)}
                          className="scale-75"
                          data-testid={`vis-client-${key}`}
                        />
                        Client
                      </label>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Render sections in order */}
      {visibleOrder.map(key => renderSection(key))}
    </div>
  );
};

export default DashboardOverview;
